import { isSelfHosted } from "@/helpers/self-hosted.helper"
import type { CommonDeployCommandProps } from "@/pages/DeploymentsPage/components/molecules/DeployCommand"
import { DEFAULT_VALUES } from "@/pages/DeploymentsPage/const"
import {
  DEFAULT_END_LINE,
  getDeployCommandString,
  getTextForValue
} from "@/pages/DeploymentsPage/helpers/deployment-command"

// The Windows command is run in PowerShell: backtick continues a line, and the
// call operator (&) runs the quoted installer path, which may contain spaces
export const WINDOWS_LINE_CONTINUATION = "`"
export const WINDOWS_INSTALLER_PREFIX = '& "'
export const WINDOWS_INSTALLER_SUFFIX = '" /S'

export const getDefaultValues = (initialValues?: {
  environment?: string
  deploymentKeyDelimiter?: string
}) => {
  const {
    environment = DEFAULT_VALUES.ENVIRONMENT,
    deploymentKeyDelimiter = "VM"
  } = initialValues || {}

  return {
    key: Date.now().toString(),
    workDir: DEFAULT_VALUES.WORKDIR,
    pathKortexTGZ: DEFAULT_VALUES.KORTEX_TGZ_PATH,
    deploymentKey: DEFAULT_VALUES.DEPLOYMENT_KEY,
    pathKortexInstaller: DEFAULT_VALUES.KORTEX_INSTALLER_PATH,
    kontigoUrl: DEFAULT_VALUES.KONTIGO_URL,
    deploymentKeyDelimiter,
    environment
  }
}

export const getLines = () => ({
  1: { text: "cd", name: "workDir", align: "space-between" as const },
  2: {
    name: "pathKortexTGZ",
    text: "tar --no-overwrite-dir -xvf",
    align: "space-between" as const
  },
  3: {
    name: "environment",
    text: "sudo ./install_kortex.sh",
    align: "space-between" as const
  },
  4: { text: "sudo systemctl start kolibri koltan" },
  5: { text: "rm", name: "pathKortexTGZ", align: "space-between" as const }
})

export const getDeployCommand = (
  values: ReturnType<typeof getDefaultValues>,
  defaultValues: ReturnType<typeof getDefaultValues>,
  commandProps?: CommonDeployCommandProps
) => {
  const lines = getLines()
  const props = { lines, values, defaultValues }
  const first = getDeployCommandString(props, ["1"])
  const second = getDeployCommandString(props, ["2"])
  const third = getDeployCommandString(props, ["3"])
  const fourth = getDeployCommandString(props, ["4"])
  const fifth = getDeployCommandString(props, ["5"])
  const suffix = `${DEFAULT_END_LINE}&& `
  // Self-hosted sensors report to the customer's Kontigo; the installer writes KONTIGO into each sensor's config
  const kontigo = isSelfHosted()
    ? `${DEFAULT_END_LINE}  KONTIGO=${getTextForValue(values.kontigoUrl, defaultValues.kontigoUrl)}`
    : ""
  const installSuffix = commandProps?.installSuffix
    ? `${DEFAULT_END_LINE}  ${commandProps.installSuffix}`
    : ""

  return `${first}${suffix}${second}${suffix}${third} ${defaultValues.deploymentKeyDelimiter} ${getTextForValue(values.deploymentKey, defaultValues.deploymentKey)}${kontigo}${installSuffix}${suffix}${fourth}${suffix}${fifth}`
}

export const getLinesWindows = () => ({
  1: {
    text: WINDOWS_INSTALLER_PREFIX,
    name: "pathKortexInstaller",
    suffix: WINDOWS_INSTALLER_SUFFIX
  },
  2: {
    name: "environment",
    text: "/ENV_NAME="
  }
})

// Linux passes extra settings as KEY=VALUE, the Windows installer as /KEY=VALUE
export const getInstallSuffixWindows = (installSuffix?: string) =>
  installSuffix ? ` /${installSuffix}` : ""

export const getDeployCommandWindows = (
  values: ReturnType<typeof getDefaultValues>,
  defaultValues: ReturnType<typeof getDefaultValues>,
  commandProps?: CommonDeployCommandProps
) => {
  const lines = getLinesWindows()
  const props = { lines, values, defaultValues }
  const installerPath = getDeployCommandString(props, ["1"])
  const envName = getDeployCommandString(props, ["2"])
  const suffix = ` ${WINDOWS_LINE_CONTINUATION}\n`
  const installSuffix = getInstallSuffixWindows(commandProps?.installSuffix)

  return `${installerPath}${suffix}${envName} /ENV_TYPE=${defaultValues.deploymentKeyDelimiter}${suffix}/DEPLOYMENT_KEY=${getTextForValue(values.deploymentKey, defaultValues.deploymentKey)}${installSuffix}`
}
